import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../../context/DataContext';
import { theme } from '../../../styles/theme';
import { Card } from '../../common/Card';
import ConfirmDialog from '../../common/ConfirmDialog';
import { formatLevelDisplay } from '../../../utils/levelUtils';

const CauseCardContainer = styled(Card)`
  display: flex;
  padding: ${theme.spacing.lg};
  position: relative;
`;

const IconContainer = styled.div`
  width: 50px;
  height: 50px;
  margin-right: ${theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CauseIcon = styled.img`
  width: 100%;
  height: auto;
`;

const ContentContainer = styled.div`
  flex: 1;
`;

const CauseName = styled.h3`
  font-size: ${theme.typography.fontSizes.large};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.deepNavy};
`;

const CauseDescription = styled.p`
  font-size: ${theme.typography.fontSizes.regular};
  color: ${theme.colors.slateGray};
  margin-bottom: ${theme.spacing.md};
`;

const LevelIndicator = styled.p`
  font-size: ${theme.typography.fontSizes.regular};
  font-weight: 700;
  color: ${theme.colors.deepNavy};
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const LastActivityText = styled.span`
  color: #808080;
  font-size: ${theme.typography.fontSizes.small};
`;

const ActionLink = styled(Link)`
  font-size: ${theme.typography.fontSizes.small};
  color: blue;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: ${theme.typography.fontSizes.small};
  position: absolute;
  top: ${theme.spacing.lg};
  left: ${theme.spacing.sm};
  padding: ${theme.spacing.xs};
  transition: all 0.2s ease-in-out;
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;

  &:hover {
    opacity: 1;
    background-color: #ffebee;
    color: #c62828;
  }

  &:focus {
    outline: 2px solid ${theme.colors.brandYellow};
    outline-offset: 2px;
  }
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${theme.colors.deepNavy};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.typography.fontSizes.small};
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  margin-bottom: 4px;
  z-index: 10;
  font-family: ${theme.typography.bodyFont};

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.deepNavy};
  }

  ${RemoveButton}:hover & {
    opacity: 1;
  }
`;


const formatActivityDate = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  if (date.getFullYear() !== now.getFullYear()) {
    return `${month} ${day}, ${date.getFullYear()}`;
  }
  return `${month} ${day}`;
};

export const CauseCard = ({ cause, lastActivityDate }) => {
  const { t } = useTranslation();
  const { title, description, level, icon, docId } = cause;
  const { removeCause } = useContext(DataContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleRemoveClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = () => {
    removeCause(docId);
  };

  return (
    <>
      <CauseCardContainer>
        <RemoveButton
          onClick={handleRemoveClick}
          aria-label={`Remove ${title} from your causes`}
        >
          <Tooltip>{t('home.removeCause')}</Tooltip>
          ✕
        </RemoveButton>
        <IconContainer>
          <CauseIcon src={icon} alt={title} />
        </IconContainer>
        <ContentContainer>
          <CauseName>{title}</CauseName>
          <CauseDescription>{description}</CauseDescription>
          <LevelIndicator>{formatLevelDisplay(level)}</LevelIndicator>
          <CardFooter>
            {lastActivityDate && (
              <LastActivityText>{t('home.lastActivity')}: {formatActivityDate(lastActivityDate)}  |</LastActivityText>
            )}
            <ActionLink to="/log-activity">{t('home.logAction')}</ActionLink>
          </CardFooter>
        </ContentContainer>
      </CauseCardContainer>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmRemove}
        title={`Remove ${title}?`}
        message={`Are you sure you want to remove "${title}" from your list?`}
        infoText="Don't worry — all your logged activities and reflections for this cause will be kept safe. You can add this cause back anytime from the Home screen."
        confirmText="Remove"
        cancelText="Cancel"
      />
    </>
  );
};
